import React, { useEffect, useRef } from "react";
import {
  View,
  StyleSheet,
  Animated,
  Dimensions,
  useWindowDimensions,
  Platform,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { COLORS } from "../../screens/tenants/tenantOnboarding/screens/components/PropertyDetailsCard";


const TableShimmer = ({ rows = 5 }) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const shimmerX = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
      Animated.loop(
        Animated.timing(shimmerX, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        })
      ),
    ]).start();
  }, []);

  const translateX = shimmerX.interpolate({
    inputRange: [0, 1],
    outputRange: [-width, width],
  });

  const SkeletonBlock = ({ style }) => (
    <View style={[styles.skeletonBase, style]} />
  );

  const renderDesktopRow = (_, index: number) => (
    <View key={index} style={styles.row}>
      <SkeletonBlock style={{ flex: 3 }} />
      <SkeletonBlock style={{ flex: 2 }} />
      <SkeletonBlock style={{ flex: 1 }} />
      <SkeletonBlock style={{ width: 30 }} />
      <SkeletonBlock style={styles.button} />
      <SkeletonBlock style={styles.button} />
    </View>
  );

  const renderMobileRow = (_, index: number) => (
    <View key={index} style={styles.mobileRow}>
      <SkeletonBlock style={{ width: "80%", height: 16 }} />
      <SkeletonBlock style={{ width: "60%", height: 14 }} />
      <View style={{ flexDirection: "row", marginTop: 10 }}>
        <SkeletonBlock style={styles.mobileButton} />
        <SkeletonBlock style={styles.mobileButton} />
      </View>
    </View>
  );

  return (
    <Animated.View style={[styles.card, { opacity: fadeAnim }]}>
      {/* Heading */}
      <View style={{ marginBottom: 20 }}>
        <SkeletonBlock style={{ width: 220, height: 22 }} />
        <SkeletonBlock style={{ width: "60%", height: 14, marginTop: 10 }} />
      </View>

      {/* Header row (desktop only) */}
      {!isMobile && (
        <View style={styles.tableHeader}>
          <SkeletonBlock style={{ flex: 3 }} />
          <SkeletonBlock style={{ flex: 2 }} />
          <SkeletonBlock style={{ flex: 1 }} />
          <SkeletonBlock style={{ width: 60 }} />
        </View>
      )}

      {/* Rows */}
      {Array.from({ length: 3 }).map(
        isMobile ? renderMobileRow : renderDesktopRow
      )}

      {/* Pagination shimmer */}
      <View style={styles.pagination}>
        <SkeletonBlock style={styles.pageDot} />
        <SkeletonBlock style={styles.pageDot} />
        <SkeletonBlock style={styles.pageDotActive} />
        <SkeletonBlock style={styles.pageDot} />
      </View>

      {/* Gradient shimmer */}
      <Animated.View
        pointerEvents="none"
        style={[
          styles.shimmerOverlay,
          { transform: [{ translateX }] },
        ]}
      >
        <LinearGradient
          colors={[
            "transparent",
            COLORS.primary + "22",
            "transparent",
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{ flex: 1 }}
        />
      </Animated.View>
    </Animated.View>
  );
};

export default TableShimmer;

const styles = StyleSheet.create({
  card: {
    marginTop: 20,
    padding: 20,
    borderRadius: 16,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#eee",
    overflow: "hidden",
  },

  skeletonBase: {
    height: 14,
    backgroundColor: "#ececec",
    borderRadius: 6,
    marginRight: 15,
  },

  tableHeader: {
    flexDirection: "row",
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f3f3",
  },

  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f3f3",
  },

  mobileRow: {
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f3f3",
  },

  button: {
    width: 90,
    height: 36,
    borderRadius: 20,
  },

  mobileButton: {
    width: 80,
    height: 34,
    borderRadius: 20,
    marginRight: 10,
  },

  shimmerOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: 200,
  },

  pagination: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 20,
  },

  pageDot: {
    width: 30,
    height: 30,
    borderRadius: 6,
    marginHorizontal: 6,
  },

  pageDotActive: {
    width: 40,
    height: 30,
    borderRadius: 6,
    marginHorizontal: 6,
  },
});